import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { whatsappService } from '../../services/api';
import '../../styles/WhatsAppDashboardMejorado.css';

function DashboardMejorado() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState(null);
  const [config, setConfig] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [mensajesPorDia, setMensajesPorDia] = useState([]);
  const [mensajesPorTipo, setMensajesPorTipo] = useState([]);

  // Colores para gráficos
  const COLORS = ['#25D366', '#FFC107', '#E74C3C', '#3498DB', '#9B59B6'];

  useEffect(() => {
    cargarDatos();
    // Actualizar cada 30 segundos
    const interval = setInterval(cargarDatos, 30000);
    return () => clearInterval(interval);
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [statsRes, configRes, mensajesRes] = await Promise.all([
        whatsappService.getEstadisticas(),
        whatsappService.getConfig(),
        whatsappService.getMensajes({})
      ]);

      setEstadisticas(statsRes.data);
      setConfig(configRes.data);
      setMensajes(mensajesRes.data.mensajes || []);

      // Procesar datos para gráficos
      procesarDatosGraficos(mensajesRes.data.mensajes || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const procesarDatosGraficos = (mensajesData) => {
    // Mensajes por día (últimos 7 días)
    const hoy = new Date();
    const dias = [];
    
    for (let i = 6; i >= 0; i--) {
      const fecha = new Date(hoy);
      fecha.setDate(fecha.getDate() - i);
      const fechaStr = fecha.toISOString().split('T')[0];
      
      const mensajesDia = mensajesData.filter(m => {
        if (!m.created_at) return false;
        const msgFecha = m.created_at.split('T')[0];
        return msgFecha === fechaStr;
      });
      
      dias.push({
        fecha: `${fecha.getDate()}/${fecha.getMonth() + 1}`,
        enviados: mensajesDia.filter(m => m.estado === 'enviado').length,
        fallidos: mensajesDia.filter(m => m.estado === 'fallido').length,
        pendientes: mensajesDia.filter(m => m.estado === 'pendiente').length
      });
    }
    setMensajesPorDia(dias);

    // Mensajes por tipo
    const tipos = {};
    mensajesData.forEach(m => {
      const tipo = m.tipo_mensaje || 'otro';
      tipos[tipo] = (tipos[tipo] || 0) + 1;
    });

    const tiposArray = Object.entries(tipos).map(([tipo, cantidad]) => ({
      name: tipo.replace('_', ' ').charAt(0).toUpperCase() + tipo.slice(1),
      value: cantidad
    }));
    setMensajesPorTipo(tiposArray);
  };

  const handleGenerarRecordatorios = async () => {
    if (!window.confirm('¿Generar recordatorios automáticos?')) return;

    try {
      setLoading(true);
      const response = await whatsappService.generarRecordatorios();
      alert(`✅ Recordatorios:\n- Creados: ${response.data.mensajes_creados}\n- Enviados: ${response.data.enviados}\n- Fallidos: ${response.data.fallidos}`);
      cargarDatos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !estadisticas) {
    return (
      <div className="dashboard-mejorado">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-mejorado">
      {/* Header */}
      <div className="page-header-mejorado">
        <div className="header-content">
          <div className="header-title">
            <h1>📱 Dashboard WhatsApp</h1>
            <p className="subtitle">Sistema de notificaciones y recordatorios</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn-generar-recordatorios"
              onClick={handleGenerarRecordatorios}
              disabled={loading}
            >
              🔔 Generar Recordatorios
            </button>
          </div>
        </div>
        {config && !config.activo && (
          <div className="alert-warning">
            ⚠️ WhatsApp desactivado. Actívalo en Configuración.
          </div>
        )}
      </div>

      {/* Métricas principales */}
      <div className="metricas-grid">
        <div className="metrica-card metrica-hoy">
          <div className="metrica-icon">📊</div>
          <div className="metrica-contenido">
            <h3>Hoy</h3>
            <div className="metrica-valor">{estadisticas?.hoy?.enviados || 0}</div>
            <p className="metrica-label">Mensajes Enviados</p>
            <div className="metrica-detalles">
              <span className="badge badge-warning">⏳ {estadisticas?.hoy?.pendientes || 0} pendientes</span>
              <span className="badge badge-danger">❌ {estadisticas?.hoy?.fallidos || 0} fallidos</span>
            </div>
          </div>
        </div>

        <div className="metrica-card metrica-semana">
          <div className="metrica-icon">📈</div>
          <div className="metrica-contenido">
            <h3>Esta Semana</h3>
            <div className="metrica-valor">{estadisticas?.semana?.enviados || 0}</div>
            <p className="metrica-label">Mensajes Enviados</p>
          </div>
        </div>

        <div className="metrica-card metrica-mes">
          <div className="metrica-icon">📅</div>
          <div className="metrica-contenido">
            <h3>Este Mes</h3>
            <div className="metrica-valor">{estadisticas?.mes?.enviados || 0}</div>
            <p className="metrica-label">Mensajes Enviados</p>
          </div>
        </div>

        <div className="metrica-card metrica-tasa">
          <div className="metrica-icon">✅</div>
          <div className="metrica-contenido">
            <h3>Tasa de Éxito</h3>
            <div className="metrica-valor">
              {estadisticas ? 
                Math.round((estadisticas.mes?.enviados / (estadisticas.mes?.enviados + (estadisticas.hoy?.fallidos || 1))) * 100) 
                : 0}%
            </div>
            <p className="metrica-label">Mensajes Exitosos</p>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="graficos-grid">
        {/* Gráfico de líneas: Mensajes por día */}
        <div className="grafico-card grafico-grande">
          <div className="grafico-header">
            <h3>📈 Mensajes por Día (Últimos 7 días)</h3>
          </div>
          <div className="grafico-contenido">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mensajesPorDia}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="fecha" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="enviados" stroke="#25D366" strokeWidth={2} name="Enviados" />
                <Line type="monotone" dataKey="fallidos" stroke="#E74C3C" strokeWidth={2} name="Fallidos" />
                <Line type="monotone" dataKey="pendientes" stroke="#FFC107" strokeWidth={2} name="Pendientes" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico de barras: Mensajes por tipo */}
        <div className="grafico-card">
          <div className="grafico-header">
            <h3>📊 Mensajes por Tipo</h3>
          </div>
          <div className="grafico-contenido">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={mensajesPorTipo}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#3498DB" name="Cantidad" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gráfico circular: Distribución de tipos */}
        <div className="grafico-card">
          <div className="grafico-header">
            <h3>🥧 Distribución de Tipos</h3>
          </div>
          <div className="grafico-contenido">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mensajesPorTipo}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {mensajesPorTipo.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="acciones-rapidas">
        <h2>⚡ Acciones Rápidas</h2>
        <div className="acciones-grid">
          <button className="accion-card" onClick={() => navigate('/whatsapp/enviar')}>
            <div className="accion-icon">✉️</div>
            <h3>Enviar Mensaje</h3>
            <p>Envío individual o masivo</p>
          </button>

          <button className="accion-card" onClick={() => navigate('/whatsapp/plantillas')}>
            <div className="accion-icon">📝</div>
            <h3>Plantillas</h3>
            <p>Gestionar mensajes reutilizables</p>
          </button>

          <button className="accion-card" onClick={() => navigate('/whatsapp/historial')}>
            <div className="accion-icon">📜</div>
            <h3>Historial</h3>
            <p>Ver mensajes enviados</p>
          </button>

          <button className="accion-card" onClick={() => navigate('/whatsapp/configuracion')}>
            <div className="accion-icon">⚙️</div>
            <h3>Configuración</h3>
            <p>Ajustes del sistema</p>
          </button>
        </div>
      </div>

      {/* Últimos mensajes */}
      <div className="ultimos-mensajes">
        <div className="seccion-header">
          <h2>📨 Últimos Mensajes</h2>
          <button 
            className="btn-ver-todos"
            onClick={() => navigate('/whatsapp/historial')}
          >
            Ver todos →
          </button>
        </div>
        <div className="mensajes-lista">
          {mensajes.slice(0, 5).map(mensaje => (
            <div key={mensaje.id} className="mensaje-item">
              <div className={`mensaje-estado estado-${mensaje.estado}`}>
                {mensaje.estado === 'enviado' ? '✅' : mensaje.estado === 'fallido' ? '❌' : '⏳'}
              </div>
              <div className="mensaje-info">
                <div className="mensaje-header">
                  <span className="mensaje-tipo">{mensaje.tipo_mensaje}</span>
                  <span className="mensaje-fecha">
                    {new Date(mensaje.created_at).toLocaleString('es-DO')}
                  </span>
                </div>
                <div className="mensaje-contenido">
                  {mensaje.mensaje.substring(0, 100)}
                  {mensaje.mensaje.length > 100 && '...'}
                </div>
                <div className="mensaje-footer">
                  <span className="mensaje-destino">📱 {mensaje.numero_destino}</span>
                </div>
              </div>
            </div>
          ))}
          {mensajes.length === 0 && (
            <div className="mensaje-vacio">
              <p>No hay mensajes registrados</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardMejorado;

